import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  ChangeDetectorRef,
  ViewChild,
  TemplateRef,
  Optional,
} from '@angular/core';
import { CustomerService } from 'src/app/shareModule/services/customer.service';
import { LocalServiceService } from 'src/app/shareModule/services/local-service.service';
import { CommonService } from 'src/app/shareModule/services/common.service';
import { SharedService } from 'src/app/shareModule/services/shared.service';
import { ToastrService } from 'ngx-toastr';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { PopUpService } from 'src/app/popupservice.service';

@Component({
  selector: 'app-share-with-others',
  templateUrl: './share-with-others.component.html',
  styleUrls: ['./share-with-others.component.scss'],
})
export class ShareWithOthersComponent implements OnInit, OnDestroy, OnChanges {
  @Input() filter: string = '';
  @Input() isAdmin: boolean = false;
  @Input() shareWithOtherTitle: string = 'Share with Others';

  @ViewChild('askQuestionAboutCategory')
  public askQuestionAboutCategoryModal: TemplateRef<any>;

  @ViewChild('questionDetailModal')
  public questionDetailModal: TemplateRef<any>;

  @ViewChild('questionsListModal')
  public questionsListModal: TemplateRef<any>;

  @ViewChild('answerModal')
  public answerModal: TemplateRef<any>;

  @ViewChild('editQuestionModal')
  public editQuestionModal: TemplateRef<any>;

  public sharedQuestions: any[] = [];
  public askQuestionText = '';
  private modalRef: NgbModalRef;

  // Create question form - matching edit-booking structure
  public createQuestionForm: any[] = [];

  // Edit question form
  public editingQuestion: any = null;
  public editQuestionForm: any[] = [];
  private editQuestionModalRef: NgbModalRef;

  private questionDetailModalRef: NgbModalRef;
  private answerModalRef: NgbModalRef | null = null;
  private answerBottomSheetRef: MatBottomSheetRef<any> | null = null;
  private destroy$ = new Subject<void>();
  public appUserId: number | null = null;

  // Question detail modal properties
  public selectedQuestion: any = null;
  public replyText = '';
  public replyingToReplyId: number | null = null;
  public replyTextForReply: Map<number, string> = new Map();

  // Inline replies for each question
  public questionRepliesMap: Map<number, any[]> = new Map();
  public replyTextMap: Map<number, string> = new Map();
  public expandedQuestions: Set<number> = new Set();

  // Answer modal properties
  public answerQuestions: any[] = [];

  // Users and answers data
  public usersWithAnswers: any = null;
  public selectedUserId: number | null = null;
  public selectedIpAddress: string | null = null;

  public imgUrl: string = environment.ImageUrl;

  get canClosePopup(): boolean {
    return !!(this.bottomSheetRef || this.activePopupRef);
  }

  public closePopup(): void {
    this.bottomSheetRef?.dismiss();
    this.activePopupRef?.dismiss();
  }

  constructor(
    private customerService: CustomerService,
    private local: LocalServiceService,
    public commonService: CommonService,
    private sharedService: SharedService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    private popup: PopUpService,
    @Optional() private bottomSheetRef?: MatBottomSheetRef<any>,
    @Optional() private activePopupRef?: NgbActiveModal,
  ) { }

  ngOnInit(): void {
    this.initUserDetails();
    if (this.filter) {
      this.fetchSharedQuestions();
    }
  }

  private initUserDetails(): void {
    this.local.getUserDetailsObserver()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user?.id) {
          this.appUserId = user.id;
        } else {
          this.appUserId = null;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload when filter changes
    if (changes['filter']) {
      this.fetchSharedQuestions();
    }
  }

  /**
   * Extract categoryId from filter string
   * Filter format: map-{categoryId} or map-{categoryId}-{typeIds}
   */
  private getCategoryIdFromFilter(): number | null {
    if (!this.filter || !this.filter.startsWith('map-')) {
      return null;
    }

    const parts = this.filter.split('-');
    if (parts.length < 2) {
      return null;
    }

    const categoryId = parseInt(parts[1], 10);
    return isNaN(categoryId) ? null : categoryId;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openCreateQuestionModal() {
    // Initialize with one empty question
    this.createQuestionForm = [{
      questionText: '',
      type: 'text',
      choices: [],
    }];
    this.modalRef = this.modalService.open(this.askQuestionAboutCategoryModal, {
      size: 'md',
      centered: true,
    });
  }

  // Question management methods (matching edit-booking)
  public addNewQuestionToForm(): void {
    this.createQuestionForm.push({
      questionText: '',
      type: 'text',
      choices: [],
    });
    this.cdr.detectChanges();
  }

  public removeQuestionFromForm(index: number): void {
    this.createQuestionForm.splice(index, 1);
    this.cdr.detectChanges();
  }

  public addChoiceToQuestion(qIndex: number): void {
    const question = this.createQuestionForm[qIndex];
    if (question.type === 'multipleChoice') {
      if (!question.choices) {
        question.choices = [];
      }
      question.choices.push('');
      this.cdr.detectChanges();
    }
  }

  public removeChoiceFromQuestion(qIndex: number, cIndex: number): void {
    const question = this.createQuestionForm[qIndex];
    if (question.choices && question.choices.length > cIndex) {
      question.choices.splice(cIndex, 1);
      this.cdr.detectChanges();
    }
  }

  public trackByChoice(index: number, choice: string): any {
    return index;
  }

  public openQuestionsListModal() {
    this.modalService.open(this.questionsListModal, {
      size: 'md',
      centered: true,
      windowClass: 'questions-list-modal',
    });
  }

  public openAnswerModal() {
    // Parse questions from shared questions
    // Questions are stored as JSON string in questionText field
    this.answerQuestions = [];

    // Show all questions from all shared questions, not limited to 3
    this.sharedQuestions.forEach((q) => {
      try {
        let parsedQuestions = q.parsedQuestions?.length
          ? q.parsedQuestions
          : this.parseQuestionTextValue(q.questionText ?? q.text);

        if (!parsedQuestions.length) {
          parsedQuestions = [{
            questionText: this.stripHtmlTags(q.text || q.questionText || 'Question'),
            type: 'text',
            choices: []
          }];
        }

        // Cache parsed questions for future use
        if (!q.parsedQuestions?.length) {
          q.parsedQuestions = parsedQuestions;
        }

        // Add each parsed question to answerQuestions
        parsedQuestions.forEach((parsedQ, parsedIndex) => {
          this.answerQuestions.push({
            questionText: parsedQ.questionText || parsedQ.question,
            type: parsedQ.type || 'text',
            choices: parsedQ.choices || [],
            answer: '',
            id: q.id,
            questionOrder: parsedIndex
          });
        });
      } catch (error) {
        console.error('Error parsing question:', error);
        // Fallback to simple text question
        this.answerQuestions.push({
          questionText: this.stripHtmlTags(q.text || q.questionText || 'Question'),
          type: 'text',
          choices: [],
          answer: '',
          id: q.id
        });
      }
    });

    // If no questions, show empty state
    if (this.answerQuestions.length === 0) {
      this.answerQuestions = [];
    }

    if (this.isMobileView()) {
      this.answerBottomSheetRef = this.popup.openPopup(this.answerModal, {
        forceMode: 'bs',
        bottomSheetPanelClass: 'answer-modal-sheet',
        hasBackdrop: true,
        noHistory: true,
      }) as MatBottomSheetRef<any>;
      this.answerModalRef = null;
    } else {
      this.answerModalRef = this.popup.openPopup(this.answerModal, {
        forceMode: 'modal',
        modalSize: 'md',
        modalIsCenter: true,
        modalWindowClass: 'answer-modal',
        modalModalClass: 'answer-modal__dialog',
        noHistory: true,
      }) as NgbModalRef;
      this.answerBottomSheetRef = null;
    }
  }

  public trackByQuestion(index: number, question: any): any {
    return question.id || index;
  }

  public submitAnswers() {
    // Validate that all questions are answered
    // const unansweredQuestions = this.answerQuestions.filter(q => !q.answer || !q.answer.trim());
    // if (unansweredQuestions.length > 0) {
    //   this.toastr.error('Please answer all questions');
    //   return;
    // }

    const uniqueQuestionIds = Array.from(
      new Set(this.answerQuestions.map((q) => q.id).filter((id): id is number => typeof id === 'number'))
    );

    if (uniqueQuestionIds.length === 0) {
      this.toastr.error('Invalid question');
      return;
    }

    if (uniqueQuestionIds.length > 1) {
      this.toastr.error('Please submit answers for one question set at a time');
      return;
    }

    const shareWithOthersId = uniqueQuestionIds[0];
    const formattedAnswers = this.answerQuestions
      .filter((q) => q.id === shareWithOthersId)
      .sort((a, b) => (a.questionOrder ?? 0) - (b.questionOrder ?? 0))
      .map((q) => {
        let answerText = q.answer;
        if (q.type === 'dateRange' && answerText.includes('-')) {
          const [fromDate, toDate] = answerText.split('-');
          answerText = `From: ${fromDate} To: ${toDate}`;
        }
        return answerText.trim();
      });

    if (!formattedAnswers.length) {
      this.toastr.error('No valid answers to submit');
      return;
    }

    const payload: any = {
      shareWithOthersId,
      text: JSON.stringify(formattedAnswers),
    };

    if (this.appUserId) {
      payload.userId = this.appUserId;
    } else {
      payload.ipAddress = this.commonService.clientIp || null;
    }

    this.customerService
      .addMapShareWithOthersReply(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Answers submitted successfully');
          // Refresh questions to show new replies
          this.fetchSharedQuestions();
          this.closeAnswerPopup();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error submitting answers:', err);
          this.toastr.error(err.error?.message || 'Something went wrong while submitting answers');
        },
      });
  }

  public openQuestionFromList(question: any) {
    // Close the list modal first
    this.modalService.dismissAll();
    // Small delay to ensure modal is closed before opening the detail modal
    setTimeout(() => {
      this.openQuestionDetailModal(question);
    }, 100);
  }

  public openQuestionDetailModal(question: any, userId?: number | null, ipAddress?: string | null) {
    this.selectedQuestion = question;
    this.selectedUserId = userId || null;
    this.selectedIpAddress = ipAddress || null;
    this.replyText = '';
    this.replyingToReplyId = null;
    this.replyTextForReply.clear();

    this.questionDetailModalRef = this.modalService.open(this.questionDetailModal, {
      size: 'md',
      centered: true,
      windowClass: 'question-detail-modal',
    });

    // Only fetch replies for the selected question
    if (question?.id) {
      this.fetchRepliesForQuestion(question.id);
    }
    this.cdr.detectChanges();
  }

  public openQuestionDetailModalForUser(userData: any) {
    if (!this.sharedQuestions || this.sharedQuestions.length === 0) {
      return;
    }
    // Get the first question (since we're using filter, there should be one question set)
    const question = this.sharedQuestions[0];
    if (question) {
      this.openQuestionDetailModal(question, userData.userId, userData.ipAddress);
    }
  }

  public addReply(questionId: number, parentId?: number) {
    if (!questionId) {
      this.toastr.error('Invalid question');
      return;
    }

    // Get reply text - either from replyTextForReply map or from replyText
    let textToSubmit = '';
    if (parentId !== undefined && parentId !== null) {
      textToSubmit = this.replyTextForReply.get(parentId) || '';
    } else {
      textToSubmit = this.replyText;
    }

    if (!textToSubmit || !textToSubmit.trim()) {
      this.toastr.error('Please enter a reply');
      return;
    }

    const payload: any = {
      shareWithOthersId: questionId,
      text: textToSubmit.trim(),
    };

    // Add userId if logged in, otherwise add ipAddress
    if (this.appUserId) {
      payload.userId = this.appUserId;
    } else {
      payload.ipAddress = this.commonService.clientIp || null;
    }

    // Add parentId if replying to a reply
    if (parentId !== undefined && parentId !== null) {
      payload.parentId = parentId;
    }

    this.customerService
      .addMapShareWithOthersReply(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Reply added');
          if (parentId !== undefined && parentId !== null) {
            this.replyTextForReply.delete(parentId);
            this.replyingToReplyId = null;
          } else {
            this.replyText = '';
          }
          this.fetchRepliesForQuestion(questionId);
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Something went wrong');
        },
      });
  }

  public startReplyingToReply(replyId: number) {
    this.replyingToReplyId = replyId;
    if (!this.replyTextForReply.has(replyId)) {
      this.replyTextForReply.set(replyId, '');
    }
    this.cdr.detectChanges();
  }

  public cancelReplyToReply(replyId: number) {
    this.replyingToReplyId = null;
    this.replyTextForReply.delete(replyId);
    this.cdr.detectChanges();
  }

  public getReplyTextForReply(replyId: number): string {
    return this.replyTextForReply.get(replyId) || '';
  }

  public setReplyTextForReply(replyId: number, text: string) {
    this.replyTextForReply.set(replyId, text);
  }

  public isReplyingToReply(replyId: number): boolean {
    return this.replyingToReplyId === replyId;
  }

  public getQuestionStyleClass(index: number): string {
    const styles = ['question-style-primary', 'question-style-secondary', 'question-style-accent'];
    return styles[index % styles.length];
  }

  private fetchSharedQuestions() {
    if (!this.filter) {
      this.sharedQuestions = [];
      this.cdr.detectChanges();
      return;
    }

    this.customerService
      .getMapShareWithOthers({ filter: this.filter })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const rawQuestions = response.body || [];
          this.sharedQuestions = rawQuestions.map((question: any) => {
            const parsedQuestions = this.parseQuestionTextValue(question?.questionText ?? question?.text);
            return {
              ...question,
              parsedQuestions,
              displayQuestionText: this.buildDisplayQuestionText(
                parsedQuestions,
                typeof question?.questionText === 'string' ? question.questionText : question?.text
              )
            };
          });
          // Fetch replies for each question
          this.sharedQuestions.forEach(question => {
            if (question.id) {
              this.fetchRepliesForQuestion(question.id);
            }
          });
          // Fetch users and answers
          this.fetchUsersAndAnswers();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error fetching shared questions:', err);
          this.sharedQuestions = [];
          this.cdr.detectChanges();
        },
      });
  }

  private fetchUsersAndAnswers() {
    if (!this.filter) {
      this.usersWithAnswers = null;
      return;
    }

    this.customerService
      .getMapShareWithOthersUsersAndAnswers({ filter: this.filter })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.usersWithAnswers = response.body || null;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error fetching users and answers:', err);
          this.usersWithAnswers = null;
          // For temporary debugging, open the answer modal
          this.cdr.detectChanges();
        },
      });
  }

  private fetchRepliesForQuestion(questionId: number) {
    this.customerService
      .getMapShareWithOthersReplies({ shareWithOthersId: questionId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const replies = this.prepareRepliesForDisplay(response.body || []);
          this.questionRepliesMap.set(questionId, replies);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error(`Error fetching replies for question ${questionId}:`, err);
          this.questionRepliesMap.set(questionId, []);
          this.cdr.detectChanges();
        },
      });
  }

  public getRepliesForQuestion(questionId: number): any[] {
    try {
      const replies = this.questionRepliesMap.get(questionId) || [];
      // If a specific user is selected, filter replies to show only that user's replies
      if (this.selectedUserId !== null) {
        return replies.filter((reply: any) => reply.userId === this.selectedUserId);
      }
      // If a specific IP address is selected (for anonymous users), filter by IP
      if (this.selectedIpAddress !== null) {
        return replies.filter((reply: any) => reply.ipAddress === this.selectedIpAddress);
      }
      return replies;
    } catch (error) {
      console.error(`Error getting replies for question ${questionId}:`, error);
      return [];
    }
  }

  public getSelectedUserName(): string | null {
    if (!this.usersWithAnswers?.usersWithAnswers) {
      return null;
    }
    const selectedUser = this.usersWithAnswers.usersWithAnswers.find((userData: any) => {
      if (this.selectedUserId !== null) {
        return userData.userId === this.selectedUserId;
      }
      if (this.selectedIpAddress !== null) {
        return userData.ipAddress === this.selectedIpAddress;
      }
      return false;
    });
    return selectedUser?.user?.profileCharacterName || 'Anonymous';
  }

  public getReplyTextForQuestion(questionId: number): string {
    return this.replyTextMap.get(questionId) || '';
  }

  public setReplyTextForQuestion(questionId: number, text: string) {
    this.replyTextMap.set(questionId, text);
  }

  public toggleQuestionExpanded(questionId: number) {
    if (this.expandedQuestions.has(questionId)) {
      this.expandedQuestions.delete(questionId);
    } else {
      this.expandedQuestions.add(questionId);
    }
    this.cdr.detectChanges();
  }

  public isQuestionExpanded(questionId: number): boolean {
    return this.expandedQuestions.has(questionId);
  }

  public addReplyToQuestion(questionId: number) {
    const replyText = this.getReplyTextForQuestion(questionId);
    if (!replyText || !replyText.trim()) {
      this.toastr.error('Please enter a reply');
      return;
    }

    const payload: any = {
      shareWithOthersId: questionId,
      text: replyText.trim(),
    };

    // Add userId if logged in, otherwise add ipAddress
    if (this.appUserId) {
      payload.userId = this.appUserId;
    } else {
      payload.ipAddress = this.commonService.clientIp || null;
    }

    this.customerService
      .addMapShareWithOthersReply(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Reply added');
          this.replyTextMap.set(questionId, '');
          this.fetchRepliesForQuestion(questionId);
          // Refresh question list to update reply count
          this.fetchSharedQuestions();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Something went wrong');
        },
      });
  }


  public addQuestionToForumMap() {
    if (!this.appUserId) {
      this.toastr.error('Please login to create a question');
      return;
    }

    // Validate all questions have text
    const invalidQuestions = this.createQuestionForm.filter(q => !q.questionText || !q.questionText.trim());
    if (invalidQuestions.length > 0) {
      this.toastr.error('Please enter question text for all questions');
      return;
    }

    // Validate multiple choice questions have at least one choice
    const invalidChoices = this.createQuestionForm.filter(q =>
      q.type === 'multipleChoice' && (!q.choices || q.choices.length === 0 || q.choices.some((c: string) => !c || !c.trim()))
    );
    if (invalidChoices.length > 0) {
      this.toastr.error('Multiple choice questions must have at least one choice');
      return;
    }

    if (!this.filter) {
      this.toastr.error('Invalid filter.');
      return;
    }

    // Send complete payload with all questions structured as JSON
    const questionsData = this.createQuestionForm.map(q => ({
      questionText: q.questionText,
      type: q.type,
      choices: q.type === 'multipleChoice' ? (q.choices || []) : undefined
    }));

    const payload = {
      text: JSON.stringify(questionsData),
      image: null,
      filter: this.filter,
    };

    this.customerService
      .addMapShareWithOthers(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Question added');
          this.createQuestionForm = [];
          this.fetchSharedQuestions();
          if (this.modalRef) {
            this.modalRef.close();
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Something went wrong');
        },
      });
  }

  private parseQuestionTextValue(questionText: any): any[] {
    if (!questionText) {
      return [];
    }

    if (Array.isArray(questionText)) {
      return questionText;
    }

    if (typeof questionText === 'object') {
      return [questionText];
    }

    if (typeof questionText === 'string') {
      try {
        const parsed = JSON.parse(questionText);
        if (Array.isArray(parsed)) {
          return parsed;
        }
        if (parsed && typeof parsed === 'object') {
          return [parsed];
        }
      } catch (error) {
        return [{
          questionText
        }];
      }
    }

    return [];
  }

  private buildDisplayQuestionText(parsedQuestions: any[], fallback?: string): string {
    if (!parsedQuestions || parsedQuestions.length === 0) {
      return fallback || '';
    }

    return parsedQuestions
      .map((item: any, index: number) => {
        const prefix = parsedQuestions.length > 1 ? `${index + 1}. ` : '';
        return `${prefix}${item?.questionText ?? item?.question ?? ''}`;
      })
      .join('<br/>');
  }

  private parseReplyTextArray(text: any): string[] {
    if (Array.isArray(text)) {
      return text;
    }
    if (typeof text === 'string') {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (error) {
        // Not JSON - treat as single string
        return [text];
      }
      return [text];
    }
    if (text === null || text === undefined) {
      return [];
    }
    return [String(text)];
  }

  private prepareRepliesForDisplay(replies: any[]): any[] {
    return replies.map((reply) => ({
      ...reply,
      parsedTextArray: this.parseReplyTextArray(reply?.text),
      replies: reply?.replies ? this.prepareRepliesForDisplay(reply.replies) : [],
    }));
  }

  public getReplyDisplayText(reply: any): string {
    const parsedAnswers = reply?.parsedTextArray || this.parseReplyTextArray(reply?.text);
    if (!parsedAnswers.length) {
      return '';
    }

    if (parsedAnswers.length === 1) {
      return this.stripHtmlTags(parsedAnswers[0]);
    }

    return parsedAnswers
      .map((ans: string, idx: number) => `${idx + 1}. ${this.stripHtmlTags(ans)}`)
      .join('<br/>');
  }

  public formatDate(date: string | Date): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return dateObj.toLocaleDateString();
  }

  public stripHtmlTags(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  public getReplyCount(question: any): number {
    if (question.replyCount === undefined || question.replyCount === null) {
      return 0;
    }
    // Convert to number in case Sequelize returns it as a string
    const count = typeof question.replyCount === 'string'
      ? parseInt(question.replyCount, 10)
      : Number(question.replyCount);
    return isNaN(count) ? 0 : count;
  }

  // Edit Question Methods
  public openEditQuestionModal(question: any) {
    this.editingQuestion = question;
    const parsedQuestions = this.parseQuestionTextValue(question?.questionText ?? question?.text);

    const normalizedQuestions = (parsedQuestions.length ? parsedQuestions : [{
      questionText: question?.questionText || question?.text || '',
      type: question?.type || 'text',
      choices: question?.choices || []
    }]).map((qn: any) => ({
      questionText: qn?.questionText || qn?.question || '',
      type: qn?.type || 'text',
      choices: qn?.type === 'multipleChoice' ? [...(qn?.choices || [])] : [],
    }));

    this.editQuestionForm = normalizedQuestions.length ? normalizedQuestions : [{
      questionText: '',
      type: 'text',
      choices: [],
    }];
    this.editQuestionModalRef = this.modalService.open(this.editQuestionModal, {
      size: 'md',
      centered: true,
    });
  }

  public saveEditQuestion() {
    if (!this.editingQuestion || !this.editQuestionForm || !this.editQuestionForm.length) {
      return;
    }

    const invalidQuestions = this.editQuestionForm.filter(
      (q) => !q.questionText || !q.questionText.trim()
    );
    if (invalidQuestions.length > 0) {
      this.toastr.error('Please enter question text for all questions');
      return;
    }

    const invalidChoices = this.editQuestionForm.filter(
      (q) =>
        q.type === 'multipleChoice' &&
        (
          !q.choices ||
          q.choices.length === 0 ||
          q.choices.some((c: string) => !c || !c.trim())
        )
    );
    if (invalidChoices.length > 0) {
      this.toastr.error('Multiple choice questions must have at least one choice and no empty choices');
      return;
    }

    const formattedQuestions = this.editQuestionForm.map((q) => ({
      questionText: q.questionText.trim(),
      type: q.type,
      ...(q.type === 'multipleChoice' ? { choices: (q.choices || []).map((c: string) => c.trim()) } : {})
    }));

    const payload = {
      questionText: JSON.stringify(formattedQuestions),
    };

    this.customerService
      .editMapShareWithOthers(this.editingQuestion.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Question updated successfully');
          this.fetchSharedQuestions();
          if (this.editQuestionModalRef) {
            this.editQuestionModalRef.close();
          }
          this.editingQuestion = null;
          this.editQuestionForm = [];
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Something went wrong');
        },
      });
  }

  public deleteQuestion(question: any) {
    if (!confirm('Are you sure you want to delete this question?')) {
      return;
    }

    this.customerService
      .deleteMapShareWithOthers(question.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Question deleted successfully');
          this.fetchSharedQuestions();
          if (this.questionDetailModalRef && this.selectedQuestion?.id === question.id) {
            this.questionDetailModalRef.close();
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Something went wrong');
        },
      });
  }

  public deleteReply(reply: any) {
    if (!confirm('Are you sure you want to delete this reply?')) {
      return;
    }

    this.customerService
      .deleteMapShareWithOthersReply(reply.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Reply deleted successfully');
          const questionId = reply?.shareWithOthersId || this.selectedQuestion?.id;
          if (questionId) {
            this.fetchRepliesForQuestion(questionId);
          }
          // Also refresh the inline replies
          this.fetchSharedQuestions();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Something went wrong');
        },
      });
  }

  public deleteUserAnswer(userData: any, event?: Event): void {
    event?.stopPropagation();
    if (!userData?.replyId) {
      this.toastr.error('Unable to delete this response');
      return;
    }

    if (!confirm('Are you sure you want to delete this response?')) {
      return;
    }

    this.customerService
      .deleteMapShareWithOthersReply(userData.replyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Response deleted successfully');
          const questionId = this.usersWithAnswers?.questionId || this.sharedQuestions?.[0]?.id;
          if (questionId) {
            this.fetchRepliesForQuestion(questionId);
          }
          this.fetchUsersAndAnswers();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Something went wrong');
        },
      });
  }

  // Edit question form helpers (same as create form)
  public addChoiceToEditQuestion(qIndex: number): void {
    if (!this.editQuestionForm || !this.editQuestionForm[qIndex]) {
      return;
    }
    const question = this.editQuestionForm[qIndex];
    if (question.type !== 'multipleChoice') {
      return;
    }
    if (!question.choices) {
      question.choices = [];
    }
    question.choices.push('');
    this.cdr.detectChanges();
  }

  public removeChoiceFromEditQuestion(qIndex: number, cIndex: number): void {
    if (!this.editQuestionForm || !this.editQuestionForm[qIndex]) {
      return;
    }
    const question = this.editQuestionForm[qIndex];
    if (!question.choices || question.choices.length <= cIndex) {
      return;
    }
    question.choices.splice(cIndex, 1);
    this.cdr.detectChanges();
  }

  public addNewQuestionToEditForm(): void {
    this.editQuestionForm.push({
      questionText: '',
      type: 'text',
      choices: [],
    });
    this.cdr.detectChanges();
  }

  public removeQuestionFromEditForm(index: number): void {
    if (this.editQuestionForm.length <= 1) {
      this.toastr.error('At least one question is required');
      return;
    }
    this.editQuestionForm.splice(index, 1);
    this.cdr.detectChanges();
  }

  private closeAnswerPopup(): void {
    if (this.answerBottomSheetRef) {
      this.answerBottomSheetRef.dismiss();
      this.answerBottomSheetRef = null;
    }
    if (this.answerModalRef) {
      this.answerModalRef.close();
      this.answerModalRef = null;
    }
  }

  private isMobileView(): boolean {
    if (!this.sharedService.isBrowser()) {
      return false;
    }
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  get popupImage(): any {
    return this.commonService.getPopupImageByFilterAndType(this.filter, 'share-with-others');
  }
}

